import {useEffect, useState, useCallback, useRef} from 'react';

type TimeOutReturnType = ReturnType<typeof setTimeout>;

const useRecursiveTimeout = (callback, delay) => {
  const [isRunning, setIsRunning] = useState(false);
  const stop = useCallback(() => setIsRunning(false), [setIsRunning]);
  const play = useCallback(() => setIsRunning(true), [setIsRunning]);
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isRunning) return;

    /**
     * type casting primitive type '0' as NodeJS.Timeout (TimeOutReturnType) type
     */
    let id: ReturnType<typeof setTimeout> = (0 as unknown) as TimeOutReturnType;

    const tick = () => {
      if (!isRunning) return clearTimeout(id);
      savedCallback.current();
      requestAnimationFrame(() => (id = setTimeout(tick, delay)));
    };
    requestAnimationFrame(() => (id = setTimeout(tick, delay)));

    return () => {
      if (id) clearTimeout(id);
      stop();
    };
  }, [isRunning, delay, stop]);

  return {play, stop};
};

export default useRecursiveTimeout;
